import React, { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/misc/Card";
import { Button } from "../../components/misc/Button";
import Spinner from "../../components/misc/Spinner";
import { cn } from "../../utils/classMerger";
import { getAppConfigs, resetAppConfig, updateAppConfig } from "../app-config/actions";
import { AppConfigItem } from "../app-config/types";
import {
    DAY_OPTIONS,
    WEEKLY_SUMMARY_CONFIG_GROUP,
    WEEKLY_SUMMARY_SEND_DAY_KEY,
    WEEKLY_SUMMARY_SEND_HOUR_KEY,
    hourLabel,
} from "./types";

const HOURS = Array.from({ length: 23 }, (_, i) => i + 1);

const extractApiError = (err: unknown, fallback: string): string => {
    const axErr = err as AxiosError<{ message?: string | string[] }>;
    const msg = axErr?.response?.data?.message;
    if (Array.isArray(msg)) return msg.join(", ");
    return msg ?? fallback;
};

const errorToast = (message: string): void => {
    toast.error(<CustomToast message={message} type="error" />, { autoClose: 3500 });
};

const selectClass = cn(
    "rounded-md border border-gray-300 px-3 py-2 text-sm bg-white",
    "focus:outline-none focus:ring-2 focus:ring-primary",
    "disabled:bg-gray-50 disabled:text-gray-400"
);

const SendSettingsCard: React.FC = () => {
    const [dayItem, setDayItem] = useState<AppConfigItem | null>(null);
    const [hourItem, setHourItem] = useState<AppConfigItem | null>(null);
    const [day, setDay] = useState<number>(1);
    const [hour, setHour] = useState<number>(8);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    const apply = useCallback((items: AppConfigItem[]): void => {
        const d = items.find((i) => i.key === WEEKLY_SUMMARY_SEND_DAY_KEY) ?? null;
        const h = items.find((i) => i.key === WEEKLY_SUMMARY_SEND_HOUR_KEY) ?? null;
        setDayItem(d);
        setHourItem(h);
        if (d) setDay(Number(d.value));
        if (h) setHour(Number(h.value));
    }, []);

    const load = useCallback(async (): Promise<void> => {
        try {
            apply(await getAppConfigs(WEEKLY_SUMMARY_CONFIG_GROUP));
        } catch (err) {
            errorToast(extractApiError(err, "Failed to load weekly summary settings"));
        }
    }, [apply]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            await load();
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [load]);

    const isAtDefault =
        !!dayItem && !!hourItem &&
        Number(dayItem.value) === Number(dayItem.defaultValue) &&
        Number(hourItem.value) === Number(hourItem.defaultValue);
    const dirty =
        !!dayItem && !!hourItem &&
        (day !== Number(dayItem.value) || hour !== Number(hourItem.value));

    const handleSave = async (): Promise<void> => {
        if (busy) return;
        setBusy(true);
        try {
            const [d, h] = await Promise.all([
                updateAppConfig(WEEKLY_SUMMARY_SEND_DAY_KEY, day),
                updateAppConfig(WEEKLY_SUMMARY_SEND_HOUR_KEY, hour),
            ]);
            apply([d, h]);
            toast.success(<CustomToast message="Weekly summary schedule saved." type="success" />, { autoClose: 3000 });
        } catch (err) {
            errorToast(extractApiError(err, "Failed to save weekly summary schedule"));
            await load();
        } finally {
            setBusy(false);
        }
    };

    const handleReset = async (): Promise<void> => {
        if (busy) return;
        setBusy(true);
        try {
            const [d, h] = await Promise.all([
                resetAppConfig(WEEKLY_SUMMARY_SEND_DAY_KEY),
                resetAppConfig(WEEKLY_SUMMARY_SEND_HOUR_KEY),
            ]);
            apply([d, h]);
            toast.success(<CustomToast message="Weekly summary schedule reset to default." type="success" />, { autoClose: 3000 });
        } catch (err) {
            errorToast(extractApiError(err, "Failed to reset weekly summary schedule"));
            await load();
        } finally {
            setBusy(false);
        }
    };

    if (!loading && (!dayItem || !hourItem)) {
        return (
            <Card>
                <CardContent className="py-6 text-sm text-gray-500">
                    Weekly summary settings are not seeded in this environment yet. Run the app-config seed and reload.
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardHeader className="pb-2">
                <CardTitle className="text-left">{dayItem?.groupLabel || "Weekly Summary"}</CardTitle>
                <p className="text-sm text-gray-500">
                    Emails go out at this hour in each user's own timezone. Changes apply from the next scheduled day.
                </p>
            </CardHeader>
            <CardContent className="pt-0">
                {loading ? (
                    <div className="py-6">
                        <Spinner loadingMessage="Loading weekly summary settings..." />
                    </div>
                ) : (
                    <div className="max-w-xl">
                        <div className="grid gap-4 min-[640px]:grid-cols-2">
                            <label className="flex flex-col gap-1 text-sm font-semibold text-primary">
                                Send day
                                <select
                                    className={selectClass}
                                    value={day}
                                    disabled={busy}
                                    onChange={(e) => setDay(Number(e.target.value))}
                                >
                                    {DAY_OPTIONS.map((o) => (
                                        <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                            </label>
                            <label className="flex flex-col gap-1 text-sm font-semibold text-primary">
                                Send hour (local)
                                <select
                                    className={selectClass}
                                    value={hour}
                                    disabled={busy}
                                    onChange={(e) => setHour(Number(e.target.value))}
                                >
                                    {HOURS.map((h) => (
                                        <option key={h} value={h}>{hourLabel(h)}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <p className="mt-3 text-xs text-gray-500">
                            The email covers the seven days before the send day.
                        </p>
                        <div className="mt-6 flex items-center gap-3 border-t border-gray-100 pt-5">
                            <Button onClick={handleSave} disabled={busy || !dirty}>
                                {busy ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={handleReset}
                                disabled={busy || isAtDefault}
                                className="border-gray-300 text-gray-700"
                            >
                                Reset to default
                            </Button>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default SendSettingsCard;
